import { useQuery } from '@tanstack/react-query';

import { displayNotif } from 'hb-react/shared/helpers/notifications';
import { axiosInstance as axios } from 'hb-react/shared/utils/axios';

/**
 * Performs a GET request and return the result.
 *
 * Example usage:
 *
 *     const foobar = useGet(Routes.some_api_path(42))?.foo?.bar;
 *
 * Some more advanced features could be added to this hook, like returning isLoading, hasError etc..
 * Or more parameters, such as to control caching and Axios cancel tokens.
 *
 * @param {string} url
 * @param {object} settings - additional configuration
 * @param {boolean} settings.enabled - to conditionally activate the hook (defaults to True)
 * @param {boolean} settings.showErrorNotification - to display all errors as an error toast notification (defaults to True)
 *
 * @return {object|null}
 */
const useGet = (
  url,
  { enabled = true, showErrorNotification = true, staleTime } = {},
) => {
  const onError = () => {
    if (showErrorNotification) {
      displayNotif('error', __('Sorry, an error occurred'));
    }
  };

  const { data = {} } = useQuery({
    queryKey: [url],
    queryFn: async () => {
      try {
        return await axios.get(url).then(({ data }) => data);
      } catch (error) {
        onError();
        throw error;
      }
    },
    enabled,
    onError,
    staleTime,
  });

  return data;
};

export default useGet;
